/**
 * Blog API contract — zod schemas (single source of truth).
 *
 * GET /v1/blog?lang=&page=&perPage=      — paginated list of published,
 *                                          is_ready post summaries.
 * GET /v1/blog/:slug?lang=               — single post detail (incl. body_html).
 *
 * Both endpoints are additive under the frozen `/v1/` contract (precedent:
 * `promo.ts`, added 2026-07-03) — no existing contract is touched.
 *
 * Source: 2026-06-25-wandero-blog-multilang-ai-authoring spec §P2.1.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Shared fragments
// ---------------------------------------------------------------------------

/** One locale alternate for a post — used to build hreflang alternates client-side. */
export const BlogPostAlternateSchema = z.object({
  lang_code: z.string().min(2).max(10),
  slug: z.string().min(1),
});
export type BlogPostAlternate = z.infer<typeof BlogPostAlternateSchema>;

// ---------------------------------------------------------------------------
// BlogPostSummarySchema — list card
// ---------------------------------------------------------------------------

export const BlogPostSummarySchema = z.object({
  id: z.number().int().positive(),
  slug: z.string().min(1),
  lang_code: z.string().min(2).max(10),
  title: z.string(),
  excerpt: z.string().nullable(),
  /**
   * Storage object path in the `wandero` bucket (blog/<uuid>.<ext>), NOT a
   * rendered URL. RESOLVED value (idea 2026-07-27-blog-image-set-as-cover):
   * `blog_posts_i18n(lang_code).cover_path` if that locale set its own
   * override, else `blog_posts.cover_path` — the api package coalesces this
   * server-side (`api/src/queries/blog.ts`), so consumers never need to know
   * about the i18n override table at all, just this one field.
   */
  cover_path: z.string().nullable(),
  /** Same resolution as `cover_path` — i18n override else parent. */
  cover_alt: z.string().nullable(),
  author: z.string().nullable(),
  published_at: z.string().datetime().nullable(),
});
export type BlogPostSummary = z.infer<typeof BlogPostSummarySchema>;

// ---------------------------------------------------------------------------
// GET /v1/blog?lang=&page=&perPage=
// ---------------------------------------------------------------------------

export const ListBlogRequestSchema = z.object({
  lang: z.string().min(2).max(10),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(12),
});
export type ListBlogRequest = z.infer<typeof ListBlogRequestSchema>;

export const ListBlogResponseSchema = z.object({
  items: BlogPostSummarySchema.array(),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  perPage: z.number().int().positive(),
});
export type ListBlogResponse = z.infer<typeof ListBlogResponseSchema>;

// ---------------------------------------------------------------------------
// BlogPostDetailSchema — full post
// ---------------------------------------------------------------------------

export const BlogPostDetailSchema = BlogPostSummarySchema.extend({
  /** TipTap editor.getHTML() output, sanitized server-side before render. */
  body_html: z.string().nullable(),
  seo_title: z.string().nullable(),
  seo_description: z.string().nullable(),
  /** Locale the post was originally authored in (blog_posts.source_lang). */
  source_lang: z.string().min(2).max(10),
  generated_at: z.string().datetime().nullable(),
  /** Other locales this post is available in (is_ready=true) — for hreflang. */
  alternates: BlogPostAlternateSchema.array().default([]),
});
export type BlogPostDetail = z.infer<typeof BlogPostDetailSchema>;

// ---------------------------------------------------------------------------
// GET /v1/blog/:slug?lang=
// ---------------------------------------------------------------------------

export const GetBlogPostRequestSchema = z.object({
  lang: z.string().min(2).max(10),
  slug: z.string().min(1),
});
export type GetBlogPostRequest = z.infer<typeof GetBlogPostRequestSchema>;

export const GetBlogPostResponseSchema = BlogPostDetailSchema;
export type GetBlogPostResponse = z.infer<typeof GetBlogPostResponseSchema>;
